import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { TrendingUp } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { AppShell } from "@/components/layout/AppShell";
import { z } from "zod";

const kpiSchema = z.object({
  name: z.string().min(2, "KPI name must be at least 2 characters").max(100, "KPI name too long"),
  unit: z.enum(["₹", "%", "#"]),
  min_band: z.number().nullable(),
  max_band: z.number().nullable(),
  delta_warn: z.number().nullable(),
  owner_user_id: z.string().uuid(),
  evidence_required: z.boolean(),
});

interface CompanyMember {
  user_id: string;
  profile_name?: string;
}

const NewKPI = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [members, setMembers] = useState<CompanyMember[]>([]);
  const [companyName, setCompanyName] = useState("");
  const [form, setForm] = useState({
    name: "",
    unit: "#" as "₹" | "%" | "#",
    min_band: "",
    max_band: "",
    delta_warn: "",
    owner_user_id: "",
    evidence_required: false,
  });

  useEffect(() => {
    loadMembers();
  }, [id]);

  const loadMembers = async () => {
    try {
      const [membersRes, companyRes] = await Promise.all([
        supabase.from("company_members").select("user_id").eq("company_id", id),
        supabase.from("companies").select("name").eq("id", id).single(),
      ]);

      if (membersRes.error) throw membersRes.error;
      setCompanyName(companyRes.data?.name || "");
      
      const membersWithProfiles = await Promise.all(
        (membersRes.data || []).map(async (member) => {
          const { data: profile } = await supabase.from("profiles_safe").select("name").eq("id", member.user_id).single();
          return { user_id: member.user_id, profile_name: profile?.name };
        })
      );

      setMembers(membersWithProfiles);
      if (membersWithProfiles.length > 0) {
        setForm((f) => ({ ...f, owner_user_id: membersWithProfiles[0].user_id }));
      }
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const validated = kpiSchema.parse({
        ...form,
        min_band: form.min_band ? parseFloat(form.min_band) : null,
        max_band: form.max_band ? parseFloat(form.max_band) : null,
        delta_warn: form.delta_warn ? parseFloat(form.delta_warn) : null,
      });

      const { error } = await supabase.from("kpis").insert([{
        company_id: id!,
        name: validated.name,
        unit: validated.unit,
        min_band: validated.min_band,
        max_band: validated.max_band,
        delta_warn: validated.delta_warn,
        owner_user_id: validated.owner_user_id,
        evidence_required: validated.evidence_required,
      }]);

      if (error) throw error;
      toast.success("KPI created successfully");
      navigate(`/app/companies/${id}`);
    } catch (err: any) {
      if (err instanceof z.ZodError) toast.error(err.errors[0].message);
      else toast.error(err.message || "Failed to create KPI");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppShell title="Add KPI" subtitle={companyName} showBack backTo={`/app/companies/${id}`} companyId={id} companyName={companyName}>
      <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="max-w-2xl mx-auto">
          <Card className="rounded-2xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Add new KPI
              </CardTitle>
              <CardDescription>Define a key metric to track weekly</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="name">KPI name</Label>
                  <Input id="name" placeholder="e.g., Monthly Recurring Revenue" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required maxLength={100} className="rounded-xl" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="unit">Unit</Label>
                  <Select value={form.unit} onValueChange={(value: "₹" | "%" | "#") => setForm({ ...form, unit: value })}>
                    <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="₹">₹ (Currency)</SelectItem>
                      <SelectItem value="%">% (Percentage)</SelectItem>
                      <SelectItem value="#"># (Number)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="min-band">Min target (green)</Label>
                    <Input id="min-band" type="number" step="any" placeholder="Optional" value={form.min_band} onChange={(e) => setForm({ ...form, min_band: e.target.value })} inputMode="decimal" className="rounded-xl" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="max-band">Max target (green)</Label>
                    <Input id="max-band" type="number" step="any" placeholder="Optional" value={form.max_band} onChange={(e) => setForm({ ...form, max_band: e.target.value })} inputMode="decimal" className="rounded-xl" />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="delta-warn">Delta warning threshold</Label>
                  <Input id="delta-warn" type="number" step="any" placeholder="Optional - warn if change exceeds this" value={form.delta_warn} onChange={(e) => setForm({ ...form, delta_warn: e.target.value })} inputMode="decimal" className="rounded-xl" />
                  <p className="text-xs text-muted-foreground">Require context if absolute delta exceeds this value</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="owner">Owner</Label>
                  <Select value={form.owner_user_id} onValueChange={(value) => setForm({ ...form, owner_user_id: value })}>
                    <SelectTrigger className="rounded-xl"><SelectValue placeholder="Select owner" /></SelectTrigger>
                    <SelectContent>
                      {members.map((member) => (
                        <SelectItem key={member.user_id} value={member.user_id}>{member.profile_name || "Unknown"}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center justify-between p-4 bg-muted/30 rounded-xl">
                  <div className="space-y-0.5">
                    <Label htmlFor="evidence">Require evidence</Label>
                    <p className="text-xs text-muted-foreground">Ask for proof/link when submitting</p>
                  </div>
                  <Switch id="evidence" checked={form.evidence_required} onCheckedChange={(checked) => setForm({ ...form, evidence_required: checked })} />
                </div>

                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-4">
                  <Button type="button" variant="outline" onClick={() => navigate(`/app/companies/${id}`)} className="flex-1 order-2 sm:order-1 rounded-xl">Cancel</Button>
                  <Button type="submit" disabled={loading} className="flex-1 order-1 sm:order-2 rounded-xl">{loading ? "Creating..." : "Create KPI"}</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </AppShell>
  );
};

export default NewKPI;
